import styled from 'styled-components';

// ─── Types ───────────────────────────────────────────────────────────────────

export type AdVariant = 'classic' | 'minimal' | 'bold';

interface AdTemplateProps {
  projectTitle: string;
  projectLocation: string;
  logoUrl: string;
  clientLogoUrl: string;
  backgroundUrl: string;
  ctaText: string;
  description: string;
  variant?: AdVariant;
}

// ─── Shared ──────────────────────────────────────────────────────────────────

const Wrapper = styled.div`
  width: 1080px;
  height: 1920px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  position: relative;
  overflow: hidden;
  background: linear-gradient(170deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
`;

const BgImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

// ═══════════════════════════════════════════════════════════════════════════════
// Variant: CLASSIC — dark overlay, centered text at bottom, yellow CTA
// ═══════════════════════════════════════════════════════════════════════════════

const ClassicOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.15) 0%,
    rgba(0, 0, 0, 0.1) 40%,
    rgba(0, 0, 0, 0.55) 70%,
    rgba(0, 0, 0, 0.85) 100%
  );
`;

const ClassicContent = styled.div`
  position: absolute;
  bottom: 120px;
  left: 80px;
  right: 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ClassicLogo = styled.img`
  position: absolute;
  top: 80px;
  left: 80px;
  right: 80px;
  z-index: 1;
  width: calc(100% - 160px);
  max-height: 160px;
  object-fit: contain;
`;

const ClassicAccent = styled.div`
  width: 80px;
  height: 4px;
  background: #ffcc00;
  margin-bottom: 48px;
`;

const ClassicTitle = styled.h1`
  font-size: 72px;
  font-weight: 700;
  color: #ffffff;
  text-align: center;
  line-height: 1.15;
  margin: 0 0 36px 0;
  max-width: 920px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
`;

const ClassicLocation = styled.p`
  font-size: 36px;
  color: rgba(255, 255, 255, 0.85);
  margin: 0 0 80px 0;
  text-align: center;
`;

const ClassicCTA = styled.div`
  background: #ffcc00;
  color: #1a1a2e;
  font-size: 36px;
  font-weight: 700;
  padding: 28px 72px;
  border-radius: 50px;
  text-transform: uppercase;
  letter-spacing: 2px;
`;

const ClassicDescription = styled.p`
  font-size: 40px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  text-align: center;
  margin: 0 0 36px 0;
  max-width: 920px;
`;

const ClassicClientLogoWrapper = styled.div`
  position: absolute;
  bottom: 40px;
  right: 80px;
  background: #ffffff;
  border-radius: 12px;
  padding: 16px 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ClassicClientLogoImg = styled.img`
  width: 200px;
  height: 100px;
  object-fit: contain;
`;

const ClassicFooter = styled.div`
  position: absolute;
  bottom: 60px;
  left: 0;
  right: 0;
  text-align: center;
  font-size: 24px;
  color: rgba(255, 255, 255, 0.4);
  letter-spacing: 4px;
  text-transform: uppercase;
`;

const ClassicTemplate = ({ projectTitle, projectLocation, logoUrl, clientLogoUrl, backgroundUrl, ctaText, description }: Omit<AdTemplateProps, 'variant'>) => (
  <>
    {backgroundUrl && <BgImage src={backgroundUrl} alt="" />}
    <ClassicOverlay />
    {logoUrl && <ClassicLogo src={logoUrl} alt="logo" />}
    <ClassicContent>
      <ClassicAccent />
      {description && <ClassicDescription>{description}</ClassicDescription>}
      <ClassicTitle>{projectTitle}</ClassicTitle>
      {projectLocation && <ClassicLocation>{projectLocation}</ClassicLocation>}
      <ClassicCTA>{ctaText}</ClassicCTA>
    </ClassicContent>
    <ClassicFooter>PROMPTO</ClassicFooter>
    {clientLogoUrl && (
      <ClassicClientLogoWrapper>
        <ClassicClientLogoImg src={clientLogoUrl} alt="client" />
      </ClassicClientLogoWrapper>
    )}
  </>
);

// ═══════════════════════════════════════════════════════════════════════════════
// Variant: MINIMAL — image top, white card bottom, left-aligned
// ═══════════════════════════════════════════════════════════════════════════════

const MinimalImageArea = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 55%;
  overflow: hidden;
`;

const MinimalCard = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 50%;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 80px 80px 120px;
  box-sizing: border-box;
`;

const MinimalAccentBar = styled.div`
  width: 6px;
  height: 80px;
  background: #ffcc00;
  margin-bottom: 40px;
`;

const MinimalTitle = styled.h1`
  font-size: 68px;
  font-weight: 700;
  color: #1a1a2e;
  line-height: 1.15;
  margin: 0 0 28px 0;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
`;

const MinimalLocation = styled.p`
  font-size: 34px;
  color: #737b8c;
  margin: 0 0 60px 0;
`;

const MinimalCTA = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: #1a1a2e;
  text-transform: uppercase;
  letter-spacing: 2px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const MinimalArrow = styled.span`
  display: inline-block;
  width: 48px;
  height: 4px;
  background: #ffcc00;
`;

const MinimalDescription = styled.p`
  font-size: 36px;
  font-weight: 500;
  color: #737b8c;
  margin: 0 0 28px 0;
`;

const MinimalLogo = styled.img`
  position: absolute;
  bottom: 50px;
  right: 80px;
  max-width: 200px;
  max-height: 60px;
  object-fit: contain;
  opacity: 0.5;
`;

const MinimalClientLogoWrapper = styled.div`
  position: absolute;
  bottom: 40px;
  left: 80px;
  background: #ffffff;
  border-radius: 10px;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const MinimalClientLogoImg = styled.img`
  width: 160px;
  height: 70px;
  object-fit: contain;
`;

const MinimalTemplate = ({ projectTitle, projectLocation, logoUrl, clientLogoUrl, backgroundUrl, ctaText, description }: Omit<AdTemplateProps, 'variant'>) => (
  <>
    <MinimalImageArea>
      {backgroundUrl && <BgImage src={backgroundUrl} alt="" />}
    </MinimalImageArea>
    <MinimalCard>
      <MinimalAccentBar />
      {description && <MinimalDescription>{description}</MinimalDescription>}
      <MinimalTitle>{projectTitle}</MinimalTitle>
      {projectLocation && <MinimalLocation>{projectLocation}</MinimalLocation>}
      <MinimalCTA>
        {ctaText}
        <MinimalArrow />
      </MinimalCTA>
    </MinimalCard>
    {logoUrl && <MinimalLogo src={logoUrl} alt="logo" />}
    {clientLogoUrl && (
      <MinimalClientLogoWrapper>
        <MinimalClientLogoImg src={clientLogoUrl} alt="client" />
      </MinimalClientLogoWrapper>
    )}
  </>
);

// ═══════════════════════════════════════════════════════════════════════════════
// Variant: BOLD — full bleed, left-aligned, heavy gradient, outline CTA
// ═══════════════════════════════════════════════════════════════════════════════

const BoldOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.05) 0%,
    rgba(0, 0, 0, 0.15) 35%,
    rgba(0, 0, 0, 0.7) 65%,
    rgba(0, 0, 0, 0.92) 100%
  );
`;

const BoldContent = styled.div`
  position: absolute;
  bottom: 140px;
  left: 80px;
  right: 80px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const BoldTitle = styled.h1`
  font-size: 84px;
  font-weight: 800;
  color: #ffffff;
  line-height: 1.1;
  margin: 0 0 16px 0;
  max-width: 920px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
`;

const BoldUnderline = styled.div`
  width: 120px;
  height: 6px;
  background: #ffcc00;
  margin-bottom: 36px;
`;

const BoldLocation = styled.p`
  font-size: 36px;
  color: rgba(255, 255, 255, 0.8);
  margin: 0 0 60px 0;
`;

const BoldCTA = styled.div`
  border: 4px solid #ffffff;
  color: #ffffff;
  font-size: 34px;
  font-weight: 700;
  padding: 24px 60px;
  border-radius: 50px;
  text-transform: uppercase;
  letter-spacing: 2px;
`;

const BoldDescription = styled.p`
  font-size: 40px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
  margin: 0 0 20px 0;
  max-width: 920px;
`;

const BoldLogo = styled.img`
  position: absolute;
  top: 80px;
  right: 80px;
  max-width: 240px;
  max-height: 100px;
  object-fit: contain;
`;

const BoldClientLogoWrapper = styled.div`
  position: absolute;
  top: 80px;
  left: 80px;
  background: #ffffff;
  border-radius: 12px;
  padding: 16px 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BoldClientLogoImg = styled.img`
  width: 180px;
  height: 90px;
  object-fit: contain;
`;

const BoldTemplate = ({ projectTitle, projectLocation, logoUrl, clientLogoUrl, backgroundUrl, ctaText, description }: Omit<AdTemplateProps, 'variant'>) => (
  <>
    {backgroundUrl && <BgImage src={backgroundUrl} alt="" />}
    <BoldOverlay />
    {logoUrl && <BoldLogo src={logoUrl} alt="logo" />}
    {clientLogoUrl && (
      <BoldClientLogoWrapper>
        <BoldClientLogoImg src={clientLogoUrl} alt="client" />
      </BoldClientLogoWrapper>
    )}
    <BoldContent>
      {description && <BoldDescription>{description}</BoldDescription>}
      <BoldTitle>{projectTitle}</BoldTitle>
      <BoldUnderline />
      {projectLocation && <BoldLocation>{projectLocation}</BoldLocation>}
      <BoldCTA>{ctaText}</BoldCTA>
    </BoldContent>
  </>
);

// ─── Main Component ──────────────────────────────────────────────────────────

const AdTemplate = ({
  variant = 'classic',
  ...props
}: AdTemplateProps) => {
  return (
    <Wrapper>
      {variant === 'classic' && <ClassicTemplate {...props} />}
      {variant === 'minimal' && <MinimalTemplate {...props} />}
      {variant === 'bold' && <BoldTemplate {...props} />}
    </Wrapper>
  );
};

export default AdTemplate;
